import React, { useId, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import FieldShell from './FieldShell';

/**
 * AppDateField — wraps PrimeReact Calendar with FieldShell.
 *
 * Works with string 'yyyy-mm-dd' format by default to avoid breaking
 * existing payloads. Set stringMode={false} to work with Date objects.
 */
const AppDateField = ({
    label,
    value,
    onChange,
    placeholder,
    helperText,
    errorMessage,
    required = false,
    invalid = false,
    disabled = false,
    readOnly = false,
    fluid = true,
    className = '',
    trailing,
    stringMode = true,
    dateFormat = 'yy-mm-dd',
    ...rest
}) => {
    const autoId = useId();
    const id = rest.id || autoId;

    // Convert string ↔ Date for stringMode
    const dateValue = (() => {
        if (!value) return null;
        if (!stringMode) return value;
        if (typeof value === 'string') {
            const d = new Date(value + 'T00:00:00');
            return isNaN(d.getTime()) ? null : d;
        }
        return value;
    })();

    const handleChange = useCallback((e) => {
        if (!stringMode) {
            onChange?.(e);
            return;
        }
        const d = e.value;
        if (!d) {
            onChange?.({ ...e, value: '' });
            return;
        }
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        onChange?.({ ...e, value: `${yyyy}-${mm}-${dd}` });
    }, [onChange, stringMode]);

    const inputCls = [
        'fk-input',
        invalid && 'fk-input--invalid',
        disabled && 'fk-input--disabled',
        readOnly && 'fk-input--readonly',
    ].filter(Boolean).join(' ');

    return (
        <FieldShell
            label={label}
            htmlFor={id}
            required={required}
            helperText={helperText}
            errorMessage={errorMessage}
            invalid={invalid}
            disabled={disabled}
            readOnly={readOnly}
            fluid={fluid}
            className={className}
            trailing={trailing}
        >
            <div className={inputCls}>
                <Calendar
                    inputId={id}
                    value={dateValue}
                    onChange={handleChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={readOnly}
                    dateFormat={dateFormat}
                    showIcon={false}
                    {...rest}
                />
            </div>
        </FieldShell>
    );
};

export default AppDateField;
